import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import CashbackWidget from '../components/CashbackWidget';
import OverviewCashback from './OverviewCashback';
import { fetchCashback } from '../actions/cashback';
import { deriveCashbackView } from '../utils/cashback';
import { trackEvent } from '../utils/analytics';
import { CASHBACK_PAYOUT_THRESHOLD } from '../constants';
import hostStyle from '../style/components/overview/OverviewCashbackWidget.module.scss';

const SWR_WINDOW_MS = 5 * 60 * 1000;

/* Analytics carry only the derived categorical state name — never a formatted balance, pending
   amount, or payout date (financial PII to a third party / Google ToS concern; SHOPWEB-695 security
   advisory). The container is the enforcement point: it never forwards widget-supplied detail
   (e.g. onError's message string) to GA. */
const CASHBACK_CATEGORY = 'Cashback';

/**
 * Flag-gated host for the v2 CashbackWidget. When `cashbackWidgetV2` is off or cashback is hidden,
 * delegates to the unchanged legacy OverviewCashback (which owns its own fetch). When on, runs a
 * 5-minute in-memory SWR fetch, derives the presentation view, and wires lifecycle callbacks to GA.
 */
const OverviewCashbackWidget = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const cashbackWidgetV2 = useSelector(
    (state) => state.featureFlags.cashbackWidgetV2,
  );
  const cashback = useSelector((state) => state.cashback);
  const { hideCashback, lastFetched } = cashback;

  const enabled = cashbackWidgetV2 && !hideCashback;

  useEffect(() => {
    if (!enabled) return;
    if (lastFetched == null || Date.now() - lastFetched > SWR_WINDOW_MS) {
      dispatch(fetchCashback());
    }
    // Re-runs whenever `enabled` flips (flag/hideCashback resolve after mount). `lastFetched` is
    // read but intentionally omitted from deps so a successful fetch updating it does not retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  if (!enabled) {
    return <OverviewCashback />;
  }

  const view = deriveCashbackView(
    { ...cashback, locale: i18n.language },
    null,
    CASHBACK_PAYOUT_THRESHOLD,
    t,
  );

  const handleViewed = ({ state }) => {
    trackEvent({
      category: CASHBACK_CATEGORY,
      action: 'Cashback Widget Viewed',
      label: state,
    });
  };

  const handleCtaClicked = ({ state }) => {
    trackEvent({
      category: CASHBACK_CATEGORY,
      action: 'Cashback Widget CTA Clicked',
      label: state,
    });
  };

  const handleError = ({ state }) => {
    trackEvent({
      category: CASHBACK_CATEGORY,
      action: 'Cashback Widget Error',
      label: state,
    });
  };

  // The host owns the My Wallet card frame (matching the sibling overview-row contract); the widget
  // renders body-only inside it (`card={false}`) so it fills its flex cell instead of drawing a
  // narrower standalone frame that overflowed the row (SHOPWEB-695).
  return (
    <div className={hostStyle.card}>
      <CashbackWidget
        {...view}
        card={false}
        onViewed={handleViewed}
        onCtaClicked={handleCtaClicked}
        onError={handleError}
      />
    </div>
  );
};

export default OverviewCashbackWidget;
